/**
 * Fuente del Web Worker de reslice oblicuo (Blob URL; CRA sin worker-loader).
 * Algoritmo empaquetado (nearest / trilineal + slab) — mantener alineado con mprObliqueReslice.js.
 */

export function getMprObliqueResliceWorkerSource() {
  return `
'use strict';
var cols = 0, rows = 0, zDim = 0, plane = 0;
var volume = null;

function sampleNearest(x, y, z) {
  var zi = Math.max(0, Math.min(zDim - 1, Math.round(z)));
  var xi = Math.max(0, Math.min(cols - 1, Math.round(x)));
  var yi = Math.max(0, Math.min(rows - 1, Math.round(y)));
  var base = zi * plane;
  return volume[base + xi + cols * yi] | 0;
}

function sampleTrilinear(x, y, z) {
  var x0 = Math.floor(x), y0 = Math.floor(y), z0 = Math.floor(z);
  var fx = x - x0, fy = y - y0, fz = z - z0;
  function cx(v) { return Math.max(0, Math.min(cols - 1, v)); }
  function cy(v) { return Math.max(0, Math.min(rows - 1, v)); }
  function cz(v) { return Math.max(0, Math.min(zDim - 1, v)); }
  var xa = cx(x0), xb = cx(x0 + 1), ya = cy(y0), yb = cy(y0 + 1);
  var za = cz(z0), zb = cz(z0 + 1);
  var b0 = za * plane, b1 = zb * plane;
  function at(base, xi, yi) { return volume[base + xi + cols * yi] | 0; }
  var c000 = at(b0, xa, ya), c100 = at(b0, xb, ya), c010 = at(b0, xa, yb), c110 = at(b0, xb, yb);
  var c001 = at(b1, xa, ya), c101 = at(b1, xb, ya), c011 = at(b1, xa, yb), c111 = at(b1, xb, yb);
  var c00 = c000 * (1 - fx) + c100 * fx;
  var c10 = c010 * (1 - fx) + c110 * fx;
  var c01 = c001 * (1 - fx) + c101 * fx;
  var c11 = c011 * (1 - fx) + c111 * fx;
  var c0 = c00 * (1 - fy) + c10 * fy;
  var c1 = c01 * (1 - fy) + c11 * fy;
  return (c0 * (1 - fz) + c1 * fz) | 0;
}

function projectSamples(proj, values) {
  if (!values.length) return 0;
  var i, m, s;
  if (proj === 'mip') {
    m = values[0];
    for (i = 1; i < values.length; i++) if (values[i] > m) m = values[i];
    return m | 0;
  }
  if (proj === 'minip') {
    m = values[0];
    for (i = 1; i < values.length; i++) if (values[i] < m) m = values[i];
    return m | 0;
  }
  if (proj === 'avg') {
    s = 0;
    for (i = 0; i < values.length; i++) s += values[i];
    return (s / values.length) | 0;
  }
  return values[(values.length / 2) | 0] | 0;
}

function resliceOne(job) {
  var outCols = Math.max(1, job.outCols | 0);
  var outRows = Math.max(1, job.outRows | 0);
  var dest = new Int16Array(outCols * outRows);
  var cx = (outCols - 1) / 2, cy = (outRows - 1) / 2;
  var u = job.u || [1, 0, 0], v = job.v || [0, 1, 0], n = job.n || [0, 0, 1];
  var ox = +job.center.x || 0, oy = +job.center.y || 0, oz = +job.center.z || 0;
  var useTri = String(job.mode || 'nearest').toLowerCase() === 'trilinear';
  var sample = useTri ? sampleTrilinear : sampleNearest;
  var kMax = Math.max(0, Math.round(+job.halfWidth || 0));
  var slabRaw = String(job.slabMode || 'thin').toLowerCase();
  var useThin = kMax === 0 || slabRaw === 'thin';
  var proj = useThin ? 'thin' : slabRaw;
  var scratch = [];
  var row, col, dr, dc, x0, y0, z0, i, k;
  for (row = 0; row < outRows; row++) {
    dr = row - cy;
    for (col = 0; col < outCols; col++) {
      dc = col - cx;
      x0 = ox + dc * u[0] + dr * v[0];
      y0 = oy + dc * u[1] + dr * v[1];
      z0 = oz + dc * u[2] + dr * v[2];
      i = col + outCols * row;
      if (useThin) {
        dest[i] = sample(x0, y0, z0);
        continue;
      }
      scratch.length = 0;
      for (k = -kMax; k <= kMax; k++) {
        scratch.push(sample(x0 + k * n[0], y0 + k * n[1], z0 + k * n[2]));
      }
      dest[i] = projectSamples(proj, scratch);
    }
  }
  return dest;
}

self.onmessage = function (ev) {
  var msg = ev.data || {};
  var id = msg.id;
  try {
    if (msg.type === 'init') {
      cols = Math.max(1, msg.cols | 0);
      rows = Math.max(1, msg.rows | 0);
      zDim = Math.max(1, msg.zDim | 0);
      plane = cols * rows;
      volume = msg.volume ? new Int16Array(msg.volume) : null;
      self.postMessage({ id: id, type: 'init-ok', ok: !!volume });
      return;
    }
    if (msg.type === 'reslice') {
      if (!volume) {
        self.postMessage({ id: id, type: 'error', error: 'no-volume' });
        return;
      }
      var jobs = msg.jobs || [];
      var planes = [];
      var transfer = [];
      for (var j = 0; j < jobs.length; j++) {
        var dest = resliceOne(jobs[j]);
        planes.push(dest.buffer);
        transfer.push(dest.buffer);
      }
      self.postMessage({ id: id, type: 'reslice-ok', planes: planes, meta: msg.meta || null }, transfer);
      return;
    }
    self.postMessage({ id: id, type: 'error', error: 'unknown' });
  } catch (e) {
    self.postMessage({ id: id, type: 'error', error: String(e && e.message || e) });
  }
};
`
}
